/**
 * <bc-crawlers count="4" kinds="dubia cricket snake"> puts live feeders on the back wall of the section it sits in.
 * They draw on a canvas behind the section's content, so they pass behind tubs and text and never cover a price.
 * `kinds` picks the cast (count defaults to its length); without it the default LINEUP is used. `also` adds kinds
 * on top of either, e.g. `also="snake"`.
 *
 * Roaches walk on six planted feet (tripod gait, two-bone legs), move in bursts, hide behind cover with only
 * their head and antennae showing, and freeze then scatter from a threat. Crickets creep on four legs and leap.
 * Larvae inch along a trail, so the body bends through every turn, and wriggle when startled. Rats trot in bursts
 * and stop to sniff, dragging a tail. Every snake brings two or three rats. It slithers in from an edge, hunts the
 * rats, strikes, coils round its catch until it stops struggling, swallows it head first and leaves. Bugs are not
 * food, but every bug and rat treats a snake head like the pointer.
 *
 * Dispatch `new CustomEvent('bc:startle', { bubbles: true, detail: { x, y } })` (client coordinates) on or inside
 * the section to scatter every bug from that point.
 *
 * Runs only while the section is on screen. With reduced motion the bugs are drawn once, resting.
 */

const TAU = Math.PI * 2;
const HOP_TIME = 0.28;
const rand = (a, b) => a + Math.random() * (b - a);
const pick = (list) => list[Math.floor(Math.random() * list.length)];
const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
const wrap = (a) => Math.atan2(Math.sin(a), Math.cos(a));
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const CALM = new Set(['roam', 'pause', 'settle', 'rest']);
// Away bugs are off the canvas and caught ones belong to a snake: neither can be startled, chased or struck.
const loose = (bug) => bug.state !== 'away' && bug.state !== 'caught';
const SNAKE_POINTS = 40;

/** Walking legs in body lengths, local frame: x forward, y to the right. Groups 0 and 1 alternate. */
const ROACH_LEGS = [
  { hip: [0.16, 0.12], rest: [0.5, 0.46] },
  { hip: [0.02, 0.15], rest: [0.0, 0.62] },
  { hip: [-0.12, 0.14], rest: [-0.52, 0.56] },
];
// The hind pair is drawn folded by drawCricket; only these four walk.
const CRICKET_LEGS = [
  { hip: [0.3, 0.08], rest: [0.62, 0.34] },
  { hip: [0.18, 0.1], rest: [0.14, 0.46] },
];
// Short legs mostly hidden under the fur; only the paws show at the flanks. Diagonal pairs step together.
const RAT_LEGS = [
  { hip: [0.22, 0.1], rest: [0.3, 0.23] },
  { hip: [-0.22, 0.12], rest: [-0.28, 0.26] },
];

const toWorld = (bug, lx, ly) => {
  const c = Math.cos(bug.heading);
  const s = Math.sin(bug.heading);
  return { x: bug.x + lx * c - ly * s, y: bug.y + lx * s + ly * c };
};
const toLocal = (bug, p) => {
  const dx = p.x - bug.x;
  const dy = p.y - bug.y;
  const c = Math.cos(bug.heading);
  const s = Math.sin(bug.heading);
  return { x: dx * c + dy * s, y: -dx * s + dy * c };
};

/** Knee of a two-bone leg from hip to foot, bent away from the body (side is -1 left, 1 right). */
function knee(hip, foot, femur, tibia, side) {
  const dx = foot.x - hip.x;
  const dy = foot.y - hip.y;
  const raw = Math.hypot(dx, dy) || 1;
  const d = clamp(raw, Math.abs(femur - tibia) + 0.01, femur + tibia - 0.01);
  const ux = dx / raw;
  const uy = dy / raw;
  const along = (femur * femur - tibia * tibia + d * d) / (2 * d);
  const h = Math.sqrt(Math.max(0, femur * femur - along * along));
  const flip = ux * side >= 0 ? 1 : -1;
  return { x: hip.x + ux * along - uy * h * flip, y: hip.y + uy * along + ux * h * flip };
}

/**
 * Body plans: how a body is planted, paced, moved and drawn. Kinds below pick one. `prey` bodies flee threats;
 * a snake is the threat. `quarry` bodies are what a snake hunts.
 */
const PLANS = {
  roach: {
    legs: ROACH_LEGS, plant: plantLegs, move: walk, pace: scuttle, draw: drawRoach,
    pause: [0.3, 1.6], start: 'mixed', wake: 'roam', peek: true, prey: true, arrive: 0.2, layer: 1,
  },
  cricket: {
    legs: CRICKET_LEGS, plant: plantLegs, move: leap, pace: hop, draw: drawCricket,
    pause: [0.5, 2.4], start: 'mixed', wake: 'roam', peek: true, prey: true, arrive: 0.9, layer: 1,
  },
  larva: {
    plant: plantTrail, move: crawl, pace: inch, draw: drawLarva,
    pause: [1, 3], start: 'placed', wake: 'roam', peek: false, prey: true, arrive: 0.2, layer: 1,
  },
  rodent: {
    legs: RAT_LEGS, plant: plantRat, move: scurry, pace: scuttle, draw: drawRat,
    pause: [0.4, 1.5], start: 'mixed', wake: 'roam', peek: true, prey: true, quarry: true, arrive: 0.2, layer: 1,
  },
  snake: {
    plant: plantTrail, move: slither, pace: glide, draw: drawSnake,
    pause: [1, 2], start: 'away', wake: 'hunt', peek: false, prey: false, arrive: 0.1, layer: 0,
  },
};

/** Each species: body plan, size (body length in px), speeds in px/s, turn rate in rad/s, colours. */
const KINDS = {
  dubia: {
    plan: PLANS.roach,
    len: [26, 40],
    cruise: [34, 64],
    dart: [230, 320],
    turn: 6,
    winged: 0.3,
    colors: { dark: '#25170c', mid: '#5b3a1f', light: '#9a6634', rim: 'rgb(214 160 90 / 0.3)', seam: 'rgb(20 12 6 / 0.75)', leg: '#3d2615', feeler: '#6a4426', wing: '#b8864c', wingDark: '#7a5129' },
  },
  cricket: {
    plan: PLANS.cricket,
    len: [22, 30],
    cruise: [10, 22],
    hop: [2.6, 4.6],
    turn: 5,
    colors: { body: '#a27a45', light: '#c99e62', dark: '#5a3e20', head: '#b48650', band: '#3a2614', wing: 'rgb(86 61 34 / 0.92)', vein: 'rgb(30 20 10 / 0.7)', veinLight: 'rgb(200 160 110 / 0.35)', leg: '#9a7444', feeler: '#7a5a34' },
  },
  waxworm: {
    plan: PLANS.larva,
    len: [30, 40],
    cruise: [12, 20],
    turn: 2.2,
    segments: 11,
    width: 0.27,
    wave: 1.1,
    colors: { body: '#ead9b0', ring: '#a98a55', head: '#6b3f1d', shine: 'rgb(255 250 235 / 0.28)' },
  },
  bsfl: {
    plan: PLANS.larva,
    len: [24, 32],
    cruise: [9, 15],
    turn: 1.8,
    segments: 11,
    width: 0.31,
    wave: 0.8,
    colors: { body: '#86744f', ring: '#3e3426', head: '#1e1912', shine: 'rgb(255 245 220 / 0.18)' },
  },
  mealworm: {
    plan: PLANS.larva,
    len: [26, 34],
    cruise: [12, 19],
    turn: 2.4,
    segments: 13,
    width: 0.17,
    wave: 1.3,
    colors: { body: '#d39a3a', ring: '#7a4c14', head: '#5e3610', shine: 'rgb(255 236 190 / 0.4)' },
  },
  superworm: {
    plan: PLANS.larva,
    len: [40, 52],
    cruise: [13, 21],
    turn: 2,
    segments: 13,
    width: 0.19,
    wave: 1,
    ringWidth: 2,
    colors: { body: '#b9874a', ring: '#3b2410', head: '#2a1809', shine: 'rgb(255 230 190 / 0.3)' },
  },
  hornworm: {
    plan: PLANS.larva,
    len: [40, 56],
    cruise: [8, 13],
    turn: 1.6,
    segments: 10,
    width: 0.3,
    wave: 0.7,
    colors: { body: '#74b24a', ring: '#4d8430', head: '#5d9a3a', shine: 'rgb(235 255 210 / 0.22)', stripe: 'rgb(240 250 225 / 0.75)', horn: '#c0472c' },
  },
  silkworm: {
    plan: PLANS.larva,
    len: [30, 42],
    cruise: [8, 14],
    turn: 1.8,
    segments: 11,
    width: 0.26,
    wave: 0.9,
    colors: { body: '#e3dfd4', ring: '#a8a294', head: '#8a8478', shine: 'rgb(255 255 255 / 0.35)' },
  },
  // Body length without the tail, which is as long again.
  rat: {
    plan: PLANS.rodent,
    len: [44, 56],
    cruise: [70, 120],
    dart: [190, 250],
    turn: 7,
    colors: { dark: '#2a1f18', mid: '#6b5545', light: '#a08672', ear: '#4f3d31', pink: '#d99a92', pinkDark: '#9c615b', whisker: 'rgb(240 228 210 / 0.5)' },
  },
  snake: {
    plan: PLANS.snake,
    len: [260, 360],
    cruise: [55, 80],
    turn: 4,
    width: 0.07,
    // Lateral wobble: amplitude in radians, wavelength in body lengths (tied to distance, not time).
    slither: [0.75, 0.45],
    // Its food comes with it, so any section with a snake also gets this many rats, low to high.
    brings: { rat: [2, 3] },
    colors: { edge: '#24160b', body: '#6e4b28', blotch: '#170e06', rim: '#94703f', head: '#4a2f18', stripe: '#a9834d', belly: '#7d5731', tongue: '#d4282f' },
  },
};
const LINEUP = ['dubia', 'cricket', 'dubia', 'waxworm', 'dubia', 'dubia', 'cricket', 'mealworm', 'dubia', 'dubia', 'cricket', 'hornworm', 'dubia', 'superworm', 'bsfl', 'silkworm'];

/**
 * What a bug is doing. Each state owns its own timers; `step` returns the next state name or nothing.
 * away: off the canvas, waiting to come back. roam: heading to a target. pause: stopped in the open.
 * settle: turning to face out of cover. rest: in cover. alarm: frozen by a threat. flee: running for cover.
 * hunt: a snake chasing the nearest quarry, or leaving when its time is up. strike: a snake's lunge.
 * constrict: a snake coiled round its catch. swallow: it lets go, then gulps the catch down head first.
 * caught: a rat held by a snake, which moves it, draws it and decides when it is gone.
 */
const STATES = {
  away: {
    enter: (bug) => (bug.timer = rand(2, 7)),
    step: (bug, dt, wall) => {
      if ((bug.timer -= dt) > 0) return;
      return wall.comeBack(bug);
    },
  },
  roam: {
    enter: (bug) => {
      bug.speed = bug.k.dart && Math.random() < 0.2 ? rand(...bug.k.dart) * 0.45 : rand(...bug.k.cruise);
      bug.timer = rand(0.8, 3);
    },
    step: (bug, dt, wall) => {
      if (wall.steer(bug, dt, bug.speed)) return arrived(bug);
      if ((bug.timer -= dt) <= 0) return 'pause';
    },
  },
  pause: {
    enter: (bug) => (bug.timer = rand(...bug.k.plan.pause)),
    step: (bug, dt, wall) => {
      wall.steer(bug, dt, 0);
      if ((bug.timer -= dt) > 0) return;
      if (dist(bug, bug.target) < bug.len) bug.target = wall.pickTarget(bug);
      return 'roam';
    },
  },
  settle: {
    step: (bug, dt) => {
      const diff = wrap(bug.target.face - bug.heading);
      bug.heading += clamp(diff, -bug.k.turn * dt, bug.k.turn * dt);
      if (Math.abs(diff) < 0.08) return 'rest';
    },
  },
  rest: {
    enter: (bug) => (bug.timer = rand(4, 12)),
    step: (bug, dt, wall) => {
      if ((bug.timer -= dt) > 0) return;
      bug.target = wall.pickTarget(bug);
      return bug.k.plan.wake;
    },
  },
  alarm: {
    enter: (bug) => (bug.timer = rand(0.15, 0.45)),
    step: (bug, dt, wall) => {
      bug.wriggle = 1;
      if ((bug.timer -= dt) > 0) return;
      bug.target = wall.escape(bug);
      return 'flee';
    },
  },
  flee: {
    enter: (bug) => (bug.speed = bug.k.dart ? rand(...bug.k.dart) : bug.k.cruise[1] * 1.8),
    step: (bug, dt, wall) => {
      if (wall.steer(bug, dt, bug.speed, 2.2)) return arrived(bug);
    },
  },
  hunt: {
    enter: (bug, from) => {
      bug.cool = rand(0.8, 2);
      bug.speed = rand(...bug.k.cruise);
      // A missed strike carries on the same hunt; a fed snake keeps the zero timer that sends it off.
      if (from !== 'strike' && from !== 'swallow') bug.timer = rand(16, 26);
    },
    step: (bug, dt, wall) => {
      bug.cool -= dt;
      if ((bug.timer -= dt) <= 0) {
        if (!bug.target.exit) bug.target = wall.exit(bug);
        if (wall.steer(bug, dt, bug.speed)) return 'away';
        return;
      }
      const prey = wall.nearestPrey(bug);
      if (prey) {
        bug.target = { x: prey.x, y: prey.y };
        if (bug.cool <= 0 && dist(bug, prey) < clamp(bug.len * 0.4, 70, 120)) {
          bug.prey = prey;
          return 'strike';
        }
      } else if (bug.target.exit || dist(bug, bug.target) < bug.len * 0.15) {
        bug.target = wall.pickTarget(bug, true);
      }
      wall.steer(bug, dt, bug.speed);
    },
  },
  strike: {
    enter: (bug) => {
      const p = bug.prey;
      bug.lunge = 0.3;
      // Aim past the prey so the head drives through it instead of stopping short.
      bug.target = { x: p.x + (p.x - bug.x) * 0.3, y: p.y + (p.y - bug.y) * 0.3 };
    },
    step: (bug, dt, wall) => {
      wall.steer(bug, dt, 420, 3);
      const p = bug.prey;
      if (loose(p) && dist(bug, p) < p.len * 0.55 + 4) {
        p.state = 'caught';
        p.target = wall.clearing(p, p.len) || wall.clearing(p, p.len * 0.6) || { x: p.x, y: p.y };
        return 'constrict';
      }
      if ((bug.lunge -= dt) <= 0) return 'hunt';
    },
  },
  constrict: {
    enter: (bug) => {
      bug.timer = rand(3.2, 4.5);
      const from = spine(bug, SNAKE_POINTS);
      bug.grip = { from, t: 0, pts: from };
    },
    step: (bug, dt) => {
      const g = bug.grip;
      const rat = bug.prey;
      g.t += dt;
      // Rats get caught on their way into cover, so the snake hauls its catch out to where the kill shows.
      const haul = dist(rat, rat.target);
      if (haul > 1) {
        const s = Math.min(1, (150 * dt) / haul);
        rat.x += (rat.target.x - rat.x) * s;
        rat.y += (rat.target.y - rat.y) * s;
      }
      // The rat fights, weakens, and lies still for the last second before the snake lets go.
      const fight = clamp((bug.timer - 1) / 1.2, 0, 1);
      rat.wriggle = fight;
      // Coils throw on from the head back, then tighten a little with every squeeze.
      const squeeze = 1.12 - 0.14 * clamp(g.t / 1.5, 0, 1) - 0.04 * fight * Math.max(0, Math.sin(g.t * 9));
      grip(bug, coil(bug, rat, squeeze), (i) => clamp((g.t - (i / SNAKE_POINTS) * 0.35) / 0.45, 0, 1));
      if ((bug.timer -= dt) <= 0) return 'swallow';
    },
  },
  swallow: {
    enter: (bug) => {
      bug.grip.from = bug.grip.pts;
      bug.grip.t = 0;
    },
    step: (bug, dt, wall) => {
      const g = bug.grip;
      const rat = bug.prey;
      if (g) {
        // The coils slacken where they are while the head comes round to the rat's nose.
        g.t += dt;
        grip(bug, coil(bug, rat, 1.04, true), (i) => clamp((g.t - (i / SNAKE_POINTS) * 0.3) / 0.6, 0, 1));
        if (g.t < 0.9) return;
        bug.trail = g.pts.slice(1);
        bug.grip = null;
        bug.timer = 3;
        return;
      }
      // The rat goes in nose first a gulp at a time, tail last; drawSnake hides whatever is past the jaws.
      const pull = ((rat.len * 2.2) / 3) * (1 + 0.8 * Math.sin(bug.timer * 9)) * dt;
      rat.x += Math.cos(rat.heading) * pull;
      rat.y += Math.sin(rat.heading) * pull;
      bug.bulge = 0.03;
      if ((bug.timer -= dt) > 0) return;
      wall.eat(bug, rat);
      return 'hunt';
    },
  },
  caught: { step: () => undefined },
};
const arrived = (bug) => (bug.target.exit ? 'away' : bug.target.face !== undefined ? 'settle' : 'pause');

/* Paces: how fast a body actually moves given the wanted speed and how far it has to turn. */
function scuttle(bug, diff, v) {
  return v * clamp(1.2 - Math.abs(diff) / 1.6, 0.15, 1);
}
function inch(bug, diff, v) {
  return v * (0.3 + 0.7 * Math.max(0, Math.sin(bug.phase * TAU)));
}
function glide(bug, diff, v) {
  return v * clamp(1.1 - Math.abs(diff) / 2.5, 0.5, 1);
}
/** Creeps, and launches a leap when lined up and the wait is over. Fleeing crickets always leap. */
function hop(bug, diff, v, dt) {
  if (bug.air) return 0;
  const h = bug.hop;
  const fleeing = bug.state === 'flee';
  const left = dist(bug, bug.target);
  if ((h.wait -= dt) <= 0 && v && Math.abs(diff) < 0.4 && (fleeing || left > bug.len * 1.2)) {
    h.t = 0;
    // Never more than a little past the target, so a hop lands inside the arrival radius.
    h.dist = Math.min(rand(...bug.k.hop) * bug.len, left + bug.len * 0.3);
    h.wait = fleeing ? rand(0.05, 0.18) : rand(0.9, 2.6);
    return 0;
  }
  return scuttle(bug, diff, v) * (fleeing ? 1 : 0.6);
}

/* Moves: what the body does every frame after the state has steered. */
/** Tripod gait: a foot steps when it trails too far behind its rest spot and the other group is planted. */
function walk(bug, dt) {
  const stride = bug.len * 0.26;
  const lead = clamp(bug.v * 0.1, 0, bug.len * 0.2);
  const swingTime = clamp((stride / Math.max(bug.v, 1)) * 0.55, 0.03, 0.14);
  const busy = [false, false];
  for (const leg of bug.legs) if (leg.swing) busy[leg.group] = true;

  for (const leg of bug.legs) {
    const goal = toWorld(bug, leg.rest.x + lead, leg.rest.y);
    if (leg.swing) {
      const s = leg.swing;
      s.t = Math.min(1, s.t + dt / s.time);
      const e = s.t * s.t * (3 - 2 * s.t);
      leg.foot = { x: s.from.x + (goal.x - s.from.x) * e, y: s.from.y + (goal.y - s.from.y) * e };
      if (s.t >= 1) leg.swing = null;
    } else if (!busy[1 - leg.group] && dist(leg.foot, goal) > stride) {
      for (const mate of bug.legs) {
        if (mate.group === leg.group && !mate.swing) mate.swing = { from: mate.foot, t: 0, time: swingTime };
      }
      busy[leg.group] = true;
    }
  }
}
/** Carries a leap to its end whatever state the cricket is in, feet tucked under, then walks. */
function leap(bug, dt) {
  const h = bug.hop;
  if (h.t < 1) {
    h.t = Math.min(1, h.t + dt / HOP_TIME);
    const step = (h.dist / HOP_TIME) * dt;
    bug.x += Math.cos(bug.heading) * step;
    bug.y += Math.sin(bug.heading) * step;
    bug.air = Math.sin(Math.PI * h.t);
    for (const leg of bug.legs) {
      leg.foot = toWorld(bug, leg.rest.x, leg.rest.y);
      leg.swing = null;
    }
    return;
  }
  bug.air = 0;
  walk(bug, dt);
}
function crawl(bug) {
  const head = bug.trail[0];
  if (dist(head, bug) < 1.5) return;
  bug.trail.unshift({ x: bug.x, y: bug.y });
  let run = 0;
  for (let i = 1; i < bug.trail.length; i++) {
    run += dist(bug.trail[i - 1], bug.trail[i]);
    if (run > bug.len * 1.6) {
      bug.trail.length = i + 1;
      break;
    }
  }
}
function slither(bug, dt) {
  crawl(bug);
  if (bug.bulge !== null && (bug.bulge += dt * 0.2) > 1.05) bug.bulge = null;
}
/** Trots on four legs and drags the tail along the path the body took. */
function scurry(bug, dt) {
  walk(bug, dt);
  crawl(bug);
}

/* Grips: while a snake holds a rat its body follows a shape built round the rat instead of its trail. */
/** Blends a gripping snake from where it was to `target`, each point when `at(i)` goes from 0 to 1. */
function grip(bug, target, at) {
  const g = bug.grip;
  g.pts = target.map((p, i) => {
    const t = at(i);
    const e = t * t * (3 - 2 * t);
    const f = g.from[i];
    return { x: f.x + (p.x - f.x) * e, y: f.y + (p.y - f.y) * e, over: e > 0.5 ? p.over : f.over };
  });
  const [head, neck] = g.pts;
  Object.assign(bug, { x: head.x, y: head.y, heading: Math.atan2(head.y - neck.y, head.x - neck.x) });
}
/**
 * A constricting snake: head clamped beside the rat's head, coils round its body, the rest curling off a flank.
 * Seen from above a coil zigzags across the rat; `over` marks its near side, drawn on top of the rat.
 * Swallowing (`gulp`) keeps the coils and puts the head on the rat's nose facing down its body, the neck folding
 * back past the nose into the first coil.
 */
function coil(bug, rat, squeeze, gulp = false) {
  const L = rat.len;
  const W = bug.len * bug.k.width;
  const r = (L * 0.2 + W * 0.7) * squeeze;
  // Short snakes (phones) still get one full coil; long ones stop at two so the rat shows between them.
  const turns = clamp((bug.len * 0.6 - L * 0.3) / (r * 5.2), 1, 2);
  const steps = Math.ceil(turns * 40);
  const fold = (j) => ({ x: L * 0.62 + W * 0.8 + (r / 2) * Math.sin((j / 8) * Math.PI), y: (-r / 2) * (1 - Math.cos((j / 8) * Math.PI)), over: true });
  const path = gulp ? [{ x: L * 0.62, y: 0, over: true }, ...Array.from({ length: 9 }, (_, j) => fold(j))] : [{ x: L * 0.36, y: -r * 1.1, over: true }];
  for (let j = 0; j <= steps; j++) {
    const a = Math.PI + (j / steps) * turns * TAU;
    // Viewed a little from the front, so each turn opens into a loop instead of folding onto itself.
    path.push({ x: L * (0.18 - (j / steps) * 0.8) + r * 0.45 * Math.sin(a), y: r * Math.cos(a), over: Math.sin(a) > 0 });
  }
  const end = path[path.length - 1];
  const side = Math.sign(end.y) || 1;
  let { x, y } = end;
  let a = Math.atan2(side, -0.5);
  for (let s = 0; s < bug.len; s += 4) {
    a -= side * 0.012;
    x += Math.cos(a) * 4;
    y += Math.sin(a) * 4;
    path.push({ x, y, over: false });
  }
  const world = path.map((p) => ({ ...toWorld(rat, p.x, p.y), over: p.over }));
  return spine({ x: world[0].x, y: world[0].y, heading: 0, trail: world, len: bug.len }, SNAKE_POINTS);
}

/* Plants: put feet or trail back under a bug that just appeared somewhere. */
function plantLegs(bug) {
  bug.legs = [];
  bug.hop.t = 1;
  bug.air = 0;
  for (const [idx, leg] of bug.k.plan.legs.entries()) {
    for (const side of [-1, 1]) {
      const hip = { x: leg.hip[0] * bug.len, y: leg.hip[1] * bug.len * side };
      const rest = { x: leg.rest[0] * bug.len, y: leg.rest[1] * bug.len * side };
      const reach = Math.hypot(rest.x - hip.x, rest.y - hip.y);
      bug.legs.push({ side, hip, rest, femur: reach * 0.55, tibia: reach * 0.62, group: (idx + (side < 0 ? 0 : 1)) % 2, foot: toWorld(bug, rest.x, rest.y), swing: null });
    }
  }
}
function plantTrail(bug) {
  bug.trail = [];
  const c = Math.cos(bug.heading);
  const s = Math.sin(bug.heading);
  for (let d = 0; d <= bug.len * 1.6; d += 2) {
    const side = Math.sin((d / bug.len) * TAU * 0.8) * bug.len * 0.05;
    bug.trail.push({ x: bug.x - c * d - s * side, y: bug.y - s * d + c * side });
  }
}
function plantRat(bug) {
  plantLegs(bug);
  plantTrail(bug);
}

/** Body points from head to tail, evenly spaced along the trail. Each keeps the `over` flag of its trail point. */
function spine(bug, n) {
  const gap = bug.len / (n - 1);
  const pts = [{ x: bug.x, y: bug.y }];
  let prev = pts[0];
  let need = gap;
  for (const p of bug.trail) {
    let d = dist(prev, p);
    while (d >= need && pts.length < n) {
      const t = need / d;
      prev = { x: prev.x + (p.x - prev.x) * t, y: prev.y + (p.y - prev.y) * t, over: p.over };
      pts.push(prev);
      d = dist(prev, p);
      need = gap;
    }
    need -= d;
    prev = p;
    if (pts.length === n) break;
  }
  while (pts.length < n) {
    const last = pts[pts.length - 1];
    pts.push({ x: last.x - Math.cos(bug.heading) * gap, y: last.y - Math.sin(bug.heading) * gap });
  }
  return pts;
}

/** Antenna angle: sweeps while stopped, points at the threat when alarmed. */
function feelerAngle(bug, side, base, sweep) {
  if (bug.state === 'alarm' && bug.threat) {
    const p = toLocal(bug, bug.threat);
    return clamp(Math.atan2(p.y, p.x), -0.9, 0.9) + side * 0.12;
  }
  return side * (base + sweep * Math.sin(bug.feeler + side * 1.3));
}

function drawRoach(ctx, bug) {
  const { len, k } = bug;
  const c = k.colors;
  const rx = len / 2;
  const ry = len * (bug.winged ? 0.27 : 0.31);

  ctx.fillStyle = 'rgb(0 0 0 / 0.28)';
  ctx.beginPath();
  ctx.ellipse(bug.x + 2, bug.y + 4, rx * 1.15, ry * 1.5, bug.heading, 0, TAU);
  ctx.fill();

  ctx.save();
  ctx.translate(bug.x, bug.y);
  ctx.rotate(bug.heading);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Legs, under the body
  ctx.strokeStyle = c.leg;
  for (const leg of bug.legs) {
    const foot = toLocal(bug, leg.foot);
    const kn = knee(leg.hip, foot, leg.femur, leg.tibia, leg.side);
    ctx.lineWidth = len * 0.07;
    ctx.beginPath();
    ctx.moveTo(leg.hip.x, leg.hip.y);
    ctx.lineTo(kn.x, kn.y);
    ctx.stroke();
    ctx.lineWidth = len * 0.04;
    ctx.beginPath();
    ctx.moveTo(kn.x, kn.y);
    ctx.lineTo(foot.x, foot.y);
    // Two spines on each shin
    for (const t of [0.4, 0.72]) {
      const sx = kn.x + (foot.x - kn.x) * t;
      const sy = kn.y + (foot.y - kn.y) * t;
      ctx.moveTo(sx, sy);
      ctx.lineTo(sx - len * 0.03, sy + leg.side * len * 0.02);
    }
    ctx.stroke();
  }

  ctx.strokeStyle = c.feeler;
  ctx.lineWidth = Math.max(0.8, len * 0.028);
  const sweep = bug.state === 'pause' || bug.state === 'rest' ? 0.32 : 0.14;
  for (const side of [-1, 1]) {
    const base = { x: len * 0.47, y: side * len * 0.07 };
    const a = feelerAngle(bug, side, 0.34, sweep);
    const reach = len * 0.95;
    ctx.beginPath();
    ctx.moveTo(base.x, base.y);
    ctx.quadraticCurveTo(
      base.x + Math.cos(a) * reach * 0.55,
      base.y + Math.sin(a) * reach * 0.55,
      base.x + Math.cos(a + side * 0.28) * reach,
      base.y + Math.sin(a + side * 0.28) * reach
    );
    ctx.stroke();
  }

  // Cerci
  ctx.strokeStyle = c.dark;
  ctx.lineWidth = len * 0.035;
  ctx.beginPath();
  for (const side of [-1, 1]) {
    ctx.moveTo(-rx * 0.94, side * len * 0.06);
    ctx.lineTo(-rx * 1.12, side * len * 0.12);
  }
  ctx.stroke();

  // Body
  const shell = ctx.createRadialGradient(len * 0.1, -ry * 0.35, len * 0.02, 0, 0, len * 0.62);
  shell.addColorStop(0, c.light);
  shell.addColorStop(0.45, c.mid);
  shell.addColorStop(1, c.dark);
  ctx.fillStyle = shell;
  ctx.beginPath();
  ctx.ellipse(0, 0, rx, ry, 0, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = c.rim;
  ctx.lineWidth = 1;
  ctx.stroke();

  if (bug.winged) {
    // Adult male: two wings over the abdomen, overlapping on the midline
    for (const side of [-1, 1]) {
      const wing = ctx.createLinearGradient(0, 0, -rx, 0);
      wing.addColorStop(0, c.wing);
      wing.addColorStop(1, c.wingDark);
      ctx.fillStyle = wing;
      ctx.beginPath();
      ctx.ellipse(-rx * 0.28, side * ry * 0.22, rx * 0.86, ry * 0.78, side * 0.05, 0, TAU);
      ctx.fill();
      ctx.strokeStyle = 'rgb(60 36 16 / 0.6)';
      ctx.lineWidth = 0.7;
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(rx * 0.2, side * ry * 0.3);
      ctx.quadraticCurveTo(-rx * 0.4, side * ry * 0.55, -rx * 1.0, side * ry * 0.2);
      ctx.stroke();
    }
  } else {
    // Nymph: plated segments with pale back edges
    for (let i = 0; i < 7; i++) {
      const x = rx * (0.28 - i * 0.18);
      const hy = ry * Math.sqrt(Math.max(0, 1 - (x / rx) ** 2)) * 0.97;
      ctx.beginPath();
      ctx.moveTo(x + len * 0.03, -hy);
      ctx.quadraticCurveTo(x - len * 0.05, 0, x + len * 0.03, hy);
      ctx.strokeStyle = c.seam;
      ctx.lineWidth = 1.1;
      ctx.stroke();
      ctx.translate(-1, 0);
      ctx.strokeStyle = 'rgb(214 160 95 / 0.22)';
      ctx.lineWidth = 0.8;
      ctx.stroke();
      ctx.translate(1, 0);
    }
  }

  // Head shield
  const shield = ctx.createRadialGradient(rx * 0.6, -ry * 0.3, 1, rx * 0.55, 0, ry * 1.1);
  shield.addColorStop(0, c.mid);
  shield.addColorStop(1, c.dark);
  ctx.fillStyle = shield;
  ctx.beginPath();
  ctx.ellipse(rx * 0.56, 0, rx * 0.42, ry * 0.96, 0, -Math.PI / 2, Math.PI / 2);
  ctx.quadraticCurveTo(rx * 0.3, 0, rx * 0.56, -ry * 0.96);
  ctx.fill();
  ctx.strokeStyle = c.rim;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = 'rgb(214 165 100 / 0.3)';
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.ellipse(rx * 0.62, side * ry * 0.38, rx * 0.12, ry * 0.18, 0, 0, TAU);
    ctx.fill();
  }

  // Gloss
  ctx.fillStyle = 'rgb(255 240 210 / 0.16)';
  ctx.beginPath();
  ctx.ellipse(rx * 0.1, -ry * 0.42, rx * 0.5, ry * 0.16, 0, 0, TAU);
  ctx.fill();
  ctx.restore();
}

function drawCricket(ctx, bug) {
  const { len: L, k } = bug;
  const c = k.colors;
  const air = bug.air;
  const h = bug.hop;
  // Hind legs kick straight back at take-off, then fold again in the air.
  const kick = h.t < 1 ? Math.sin(Math.PI * Math.min(1, h.t / 0.45)) : 0;

  // Shadow stays on the ground and fades as the body lifts away from it
  ctx.fillStyle = `rgb(0 0 0 / ${0.3 - air * 0.14})`;
  ctx.beginPath();
  ctx.ellipse(bug.x + 2 + air * 5, bug.y + 4 + air * 12, L * 0.6 * (1 - air * 0.25), L * 0.24 * (1 - air * 0.25), bug.heading, 0, TAU);
  ctx.fill();

  ctx.save();
  ctx.translate(bug.x, bug.y - air * L * 0.55);
  ctx.rotate(bug.heading);
  const grow = 1 + air * 0.2;
  ctx.scale(grow, grow);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Walking legs
  ctx.strokeStyle = c.leg;
  for (const leg of bug.legs) {
    const foot = toLocal(bug, leg.foot);
    const kn = knee(leg.hip, foot, leg.femur, leg.tibia, leg.side);
    ctx.lineWidth = L * 0.05;
    ctx.beginPath();
    ctx.moveTo(leg.hip.x, leg.hip.y);
    ctx.lineTo(kn.x, kn.y);
    ctx.stroke();
    ctx.lineWidth = L * 0.03;
    ctx.beginPath();
    ctx.moveTo(kn.x, kn.y);
    ctx.lineTo(foot.x, foot.y);
    ctx.stroke();
  }

  // Jumping legs: a drumstick femur along each flank, the shin folded forward beside it
  for (const side of [-1, 1]) {
    const hip = { x: L * 0.04, y: side * L * 0.11 };
    const kn = { x: L * (-0.54 + kick * 0.1), y: side * L * (0.21 - kick * 0.04) };
    const foot = { x: L * (-0.12 - kick * 0.95), y: side * L * (0.28 - kick * 0.05) };
    ctx.strokeStyle = c.dark;
    ctx.lineWidth = L * 0.03;
    ctx.beginPath();
    ctx.moveTo(kn.x, kn.y);
    ctx.lineTo(foot.x, foot.y);
    ctx.lineTo(foot.x + (kick ? -L * 0.09 : L * 0.03), foot.y + side * L * 0.05);
    for (const t of [0.35, 0.65]) {
      const sx = kn.x + (foot.x - kn.x) * t;
      const sy = kn.y + (foot.y - kn.y) * t;
      ctx.moveTo(sx, sy);
      ctx.lineTo(sx - L * 0.02, sy + side * L * 0.03);
    }
    ctx.stroke();
    const flen = Math.hypot(kn.x - hip.x, kn.y - hip.y);
    ctx.save();
    ctx.translate(hip.x, hip.y);
    ctx.rotate(Math.atan2(kn.y - hip.y, kn.x - hip.x));
    const femur = ctx.createLinearGradient(0, -L * 0.09, 0, L * 0.09);
    femur.addColorStop(0, side < 0 ? c.dark : c.light);
    femur.addColorStop(1, side < 0 ? c.light : c.dark);
    ctx.fillStyle = femur;
    ctx.beginPath();
    ctx.moveTo(0, -L * 0.045);
    ctx.bezierCurveTo(flen * 0.25, -L * 0.1, flen * 0.6, -L * 0.055, flen, -L * 0.018);
    ctx.lineTo(flen, L * 0.018);
    ctx.bezierCurveTo(flen * 0.6, L * 0.055, flen * 0.25, L * 0.1, 0, L * 0.045);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = c.vein;
    ctx.lineWidth = 0.6;
    ctx.beginPath();
    ctx.moveTo(flen * 0.12, 0);
    ctx.lineTo(flen * 0.88, 0);
    ctx.stroke();
    ctx.restore();
  }

  // Very long antennae, swaying
  ctx.strokeStyle = c.feeler;
  ctx.lineWidth = Math.max(0.5, L * 0.018);
  for (const side of [-1, 1]) {
    const bx = L * 0.54;
    const by = side * L * 0.04;
    const a = feelerAngle(bug, side, 0.18, bug.state === 'rest' || bug.state === 'pause' ? 0.16 : 0.08);
    const sway = 0.22 * Math.sin(bug.feeler * 0.7 + side);
    const reach = L * 1.6;
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.bezierCurveTo(
      bx + Math.cos(a) * reach * 0.35, by + Math.sin(a) * reach * 0.35,
      bx + Math.cos(a + side * 0.15 + sway) * reach * 0.7, by + Math.sin(a + side * 0.15 + sway) * reach * 0.7,
      bx + Math.cos(a + side * 0.3 + sway * 1.6) * reach, by + Math.sin(a + side * 0.3 + sway * 1.6) * reach
    );
    ctx.stroke();
  }

  // Cerci
  ctx.strokeStyle = c.dark;
  ctx.lineWidth = L * 0.025;
  ctx.beginPath();
  for (const side of [-1, 1]) {
    ctx.moveTo(-L * 0.46, side * L * 0.05);
    ctx.quadraticCurveTo(-L * 0.62, side * L * 0.1, -L * 0.8, side * L * 0.17);
  }
  ctx.stroke();

  // Abdomen
  const belly = ctx.createRadialGradient(-L * 0.1, -L * 0.06, 1, -L * 0.14, 0, L * 0.38);
  belly.addColorStop(0, c.light);
  belly.addColorStop(1, c.body);
  ctx.fillStyle = belly;
  ctx.beginPath();
  ctx.ellipse(-L * 0.14, 0, L * 0.35, L * 0.18, 0, 0, TAU);
  ctx.fill();

  // Folded wings over the abdomen, overlapping on the midline, with a few veins
  ctx.fillStyle = c.wing;
  ctx.beginPath();
  ctx.moveTo(L * 0.18, -L * 0.13);
  ctx.quadraticCurveTo(-L * 0.2, -L * 0.165, -L * 0.45, -L * 0.1);
  ctx.quadraticCurveTo(-L * 0.53, 0, -L * 0.45, L * 0.1);
  ctx.quadraticCurveTo(-L * 0.2, L * 0.165, L * 0.18, L * 0.13);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = c.vein;
  ctx.lineWidth = 0.7;
  ctx.beginPath();
  ctx.moveTo(L * 0.18, 0);
  ctx.lineTo(-L * 0.5, 0);
  for (const side of [-1, 1]) {
    ctx.moveTo(L * 0.16, side * L * 0.07);
    ctx.quadraticCurveTo(-L * 0.15, side * L * 0.1, -L * 0.44, side * L * 0.05);
    for (const x of [0.1, 0.02, -0.06]) {
      ctx.moveTo(L * x, side * L * 0.015);
      ctx.lineTo(L * (x - 0.06), side * L * 0.075);
    }
  }
  ctx.stroke();
  ctx.strokeStyle = c.veinLight;
  ctx.beginPath();
  for (const side of [-1, 1]) {
    ctx.moveTo(L * 0.16, side * L * 0.125);
    ctx.quadraticCurveTo(-L * 0.2, side * L * 0.15, -L * 0.43, side * L * 0.09);
  }
  ctx.stroke();

  // Pronotum, a saddle behind the head
  ctx.fillStyle = c.body;
  ctx.beginPath();
  ctx.roundRect(L * 0.16, -L * 0.14, L * 0.2, L * 0.28, L * 0.05);
  ctx.fill();
  ctx.fillStyle = c.dark;
  ctx.beginPath();
  ctx.ellipse(L * 0.26, -L * 0.06, L * 0.06, L * 0.035, 0.3, 0, TAU);
  ctx.ellipse(L * 0.26, L * 0.06, L * 0.06, L * 0.035, -0.3, 0, TAU);
  ctx.fill();

  // Round head with the house cricket's dark band between the eyes
  ctx.fillStyle = c.head;
  ctx.beginPath();
  ctx.arc(L * 0.44, 0, L * 0.115, 0, TAU);
  ctx.fill();
  ctx.fillStyle = c.band;
  ctx.beginPath();
  ctx.ellipse(L * 0.42, 0, L * 0.035, L * 0.1, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = '#140c05';
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.ellipse(L * 0.46, side * L * 0.085, L * 0.035, L * 0.025, 0, 0, TAU);
    ctx.fill();
  }

  ctx.fillStyle = 'rgb(255 240 210 / 0.18)';
  ctx.beginPath();
  ctx.ellipse(-L * 0.05, -L * 0.07, L * 0.25, L * 0.03, 0, 0, TAU);
  ctx.fill();
  ctx.restore();
}

function drawLarva(ctx, bug, time) {
  const { len, k } = bug;
  const c = k.colors;
  const n = k.segments;
  const pts = spine(bug, n);
  const widths = pts.map((_, i) => {
    const t = i / (n - 1);
    const taper = t < 0.15 ? 0.75 + (t / 0.15) * 0.25 : t > 0.7 ? 1 - ((t - 0.7) / 0.3) * 0.4 : 1;
    return len * k.width * taper * (1 + 0.1 * Math.sin(bug.phase * TAU - i * 0.75));
  });
  // Startled larvae thrash side to side, strongest at the head
  if (bug.wriggle) {
    for (let i = 0; i < n; i++) {
      const a = pts[Math.max(0, i - 1)];
      const b = pts[Math.min(n - 1, i + 1)];
      const along = Math.atan2(a.y - b.y, a.x - b.x);
      const push = Math.sin(time * 16 - i * 0.7) * len * 0.09 * bug.wriggle * (1 - i / n);
      pts[i] = { x: pts[i].x - Math.sin(along) * push, y: pts[i].y + Math.cos(along) * push };
    }
  }
  const angle = (i) => {
    const a = pts[Math.max(0, i - 1)];
    const b = pts[Math.min(n - 1, i + 1)];
    return Math.atan2(a.y - b.y, a.x - b.x);
  };

  ctx.fillStyle = 'rgb(0 0 0 / 0.3)';
  for (let i = 0; i < n; i++) {
    ctx.beginPath();
    ctx.arc(pts[i].x + 2, pts[i].y + 3, widths[i] * 0.62, 0, TAU);
    ctx.fill();
  }
  if (c.horn) {
    // Tail horn, curving up and back
    const a = angle(n - 1);
    const tail = pts[n - 1];
    const hl = len * 0.16;
    ctx.strokeStyle = c.horn;
    ctx.lineWidth = Math.max(1.2, len * 0.035);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(tail.x, tail.y);
    ctx.quadraticCurveTo(
      tail.x - Math.cos(a) * hl * 0.6 + Math.sin(a) * hl * 0.15,
      tail.y - Math.sin(a) * hl * 0.6 - Math.cos(a) * hl * 0.15,
      tail.x - Math.cos(a + 0.5) * hl,
      tail.y - Math.sin(a + 0.5) * hl
    );
    ctx.stroke();
  }
  ctx.fillStyle = c.body;
  for (let i = n - 1; i >= 0; i--) {
    ctx.beginPath();
    ctx.arc(pts[i].x, pts[i].y, widths[i] / 2, 0, TAU);
    ctx.fill();
  }
  ctx.strokeStyle = c.ring;
  ctx.lineWidth = k.ringWidth || 0.9;
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (let i = 1; i < n - 1; i++) {
    const along = angle(i);
    const half = widths[i] * 0.46;
    ctx.moveTo(pts[i].x - Math.sin(along) * half, pts[i].y + Math.cos(along) * half);
    ctx.lineTo(pts[i].x + Math.sin(along) * half, pts[i].y - Math.cos(along) * half);
  }
  ctx.stroke();
  if (c.stripe) {
    // Hornworm: a pale oblique stripe down each flank of every segment
    ctx.strokeStyle = c.stripe;
    ctx.lineWidth = Math.max(1, len * 0.02);
    ctx.beginPath();
    for (let i = 2; i < n - 1; i++) {
      const a = angle(i);
      const half = widths[i] * 0.45;
      const back = len / (n - 1);
      for (const side of [-1, 1]) {
        const nx = -Math.sin(a) * side;
        const ny = Math.cos(a) * side;
        ctx.moveTo(pts[i].x + nx * half * 0.85, pts[i].y + ny * half * 0.85);
        ctx.lineTo(pts[i].x + nx * half * 0.2 - Math.cos(a) * back * 0.9, pts[i].y + ny * half * 0.2 - Math.sin(a) * back * 0.9);
      }
    }
    ctx.stroke();
  }
  ctx.fillStyle = c.shine;
  for (let i = 1; i < n - 1; i++) {
    ctx.beginPath();
    ctx.arc(pts[i].x - widths[i] * 0.1, pts[i].y - widths[i] * 0.16, widths[i] * 0.2, 0, TAU);
    ctx.fill();
  }

  const along = angle(0);
  ctx.fillStyle = c.head;
  ctx.beginPath();
  ctx.ellipse(pts[0].x + Math.cos(along) * widths[0] * 0.35, pts[0].y + Math.sin(along) * widths[0] * 0.35, widths[0] * 0.38, widths[0] * 0.32, along, 0, TAU);
  ctx.fill();
}

function drawRat(ctx, bug, time) {
  const { len: L, k } = bug;
  const c = k.colors;
  const fight = bug.state === 'caught' ? bug.wriggle : 0;
  const sniff = bug.state === 'pause' || bug.state === 'rest' ? Math.sin(time * 19 + bug.phase * TAU) : 0;

  ctx.fillStyle = 'rgb(0 0 0 / 0.28)';
  ctx.beginPath();
  ctx.ellipse(bug.x + 2, bug.y + 4, L * 0.58, L * 0.26, bug.heading, 0, TAU);
  ctx.fill();

  // Tail: starts at the rump, follows the path the body took, sways at rest and lashes when held
  const tail = spine({ x: bug.x, y: bug.y, heading: bug.heading, trail: bug.trail, len: L * 1.5 }, 16).slice(4);
  tail[0] = toWorld(bug, -L * 0.44, 0);
  const m = tail.length - 1;
  for (let j = 1; j <= m; j++) {
    const a = tail[j - 1];
    const b = tail[j];
    const along = Math.atan2(b.y - a.y, b.x - a.x);
    const push = Math.sin(time * (fight ? 13 : 2.4) + j * 0.55 + bug.phase * TAU) * L * (0.025 + 0.1 * fight) * (j / m);
    tail[j] = { x: b.x - Math.sin(along) * push, y: b.y + Math.cos(along) * push };
  }
  ctx.lineCap = 'round';
  for (const [color, scale] of [[c.pinkDark, 1], [c.pink, 0.55]]) {
    ctx.strokeStyle = color;
    for (let j = 0; j < m; j++) {
      ctx.lineWidth = L * (0.075 - (0.055 * j) / m) * scale;
      ctx.beginPath();
      ctx.moveTo(tail[j].x, tail[j].y);
      ctx.lineTo(tail[j + 1].x, tail[j + 1].y);
      ctx.stroke();
    }
  }

  ctx.save();
  ctx.translate(bug.x, bug.y);
  ctx.rotate(bug.heading + Math.sin(time * 21) * 0.14 * fight);
  ctx.lineJoin = 'round';

  // Legs: a dark limb to a pink paw at the flank, paddling when held
  for (const [idx, leg] of bug.legs.entries()) {
    const foot = toLocal(bug, leg.foot);
    const kick = Math.sin(time * 30 + idx * 1.7) * L * 0.09 * fight;
    const paw = { x: foot.x + kick, y: foot.y + leg.side * Math.abs(kick) * 0.5 };
    ctx.strokeStyle = c.dark;
    ctx.lineWidth = L * 0.07;
    ctx.beginPath();
    ctx.moveTo(leg.hip.x, leg.hip.y);
    ctx.lineTo(paw.x, paw.y);
    ctx.stroke();
    ctx.fillStyle = c.pink;
    ctx.beginPath();
    ctx.ellipse(paw.x, paw.y, L * 0.045, L * 0.032, 0, 0, TAU);
    ctx.fill();
  }

  // Body: pear-shaped from the rump to a pointed snout, lit from the upper left
  const fur = ctx.createRadialGradient(-L * 0.02, -L * 0.07, L * 0.02, 0, 0, L * 0.55);
  fur.addColorStop(0, c.light);
  fur.addColorStop(0.5, c.mid);
  fur.addColorStop(1, c.dark);
  ctx.fillStyle = fur;
  ctx.beginPath();
  ctx.moveTo(L * 0.6, 0);
  ctx.bezierCurveTo(L * 0.52, -L * 0.08, L * 0.34, -L * 0.13, L * 0.16, -L * 0.16);
  ctx.bezierCurveTo(-L * 0.06, -L * 0.23, -L * 0.4, -L * 0.25, -L * 0.48, -L * 0.09);
  ctx.quadraticCurveTo(-L * 0.54, 0, -L * 0.48, L * 0.09);
  ctx.bezierCurveTo(-L * 0.4, L * 0.25, -L * 0.06, L * 0.23, L * 0.16, L * 0.16);
  ctx.bezierCurveTo(L * 0.34, L * 0.13, L * 0.52, L * 0.08, L * 0.6, 0);
  ctx.fill();

  // Ears: round, pink inside, pricked forward while sniffing
  for (const side of [-1, 1]) {
    const ex = L * (0.22 + sniff * 0.01);
    const ey = side * L * 0.135;
    ctx.fillStyle = c.ear;
    ctx.beginPath();
    ctx.ellipse(ex, ey, L * 0.07, L * 0.085, side * 0.5, 0, TAU);
    ctx.fill();
    ctx.fillStyle = c.pinkDark;
    ctx.beginPath();
    ctx.ellipse(ex + L * 0.01, ey + side * L * 0.01, L * 0.042, L * 0.055, side * 0.5, 0, TAU);
    ctx.fill();
  }

  // Eyes, nose and whiskers, which twitch while sniffing
  for (const side of [-1, 1]) {
    ctx.fillStyle = '#0b0705';
    ctx.beginPath();
    ctx.arc(L * 0.4, side * L * 0.075, L * 0.03, 0, TAU);
    ctx.fill();
    ctx.fillStyle = 'rgb(255 245 230 / 0.6)';
    ctx.beginPath();
    ctx.arc(L * 0.41, side * L * 0.075 - L * 0.01, L * 0.01, 0, TAU);
    ctx.fill();
  }
  ctx.fillStyle = c.pink;
  ctx.beginPath();
  ctx.arc(L * (0.6 + sniff * 0.012), 0, L * 0.028, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = c.whisker;
  ctx.lineWidth = 0.6;
  ctx.beginPath();
  for (const side of [-1, 1]) {
    for (const spread of [0.35, 0.75, 1.15]) {
      const a = side * (spread + sniff * 0.12 + fight * Math.sin(time * 25) * 0.3);
      ctx.moveTo(L * 0.54, side * L * 0.04);
      ctx.lineTo(L * 0.54 + Math.cos(a) * L * 0.3, side * L * 0.04 + Math.sin(a) * L * 0.3);
    }
  }
  ctx.stroke();

  ctx.fillStyle = 'rgb(255 240 215 / 0.14)';
  ctx.beginPath();
  ctx.ellipse(-L * 0.05, -L * 0.08, L * 0.28, L * 0.05, 0, 0, TAU);
  ctx.fill();
  ctx.restore();
}

function drawSnake(ctx, bug, time) {
  const { len, k } = bug;
  const c = k.colors;
  const n = SNAKE_POINTS;
  const pts = bug.grip ? bug.grip.pts : spine(bug, n);
  // A held rat is drawn here, not with the other bugs: between the coils' far and near sides, or under the
  // whole snake while it goes down, cut off at the jaws.
  const held = bug.state === 'constrict' || bug.state === 'swallow' ? bug.prey : null;
  if (held && !bug.grip) {
    const a = Math.atan2(pts[0].y - pts[1].y, pts[0].x - pts[1].x);
    ctx.save();
    ctx.translate(pts[0].x, pts[0].y);
    ctx.rotate(a);
    ctx.beginPath();
    ctx.rect(0, -held.len * 3, held.len * 4, held.len * 6);
    ctx.clip();
    ctx.rotate(-a);
    ctx.translate(-pts[0].x, -pts[0].y);
    held.k.plan.draw(ctx, held, time);
    ctx.restore();
  }
  const W = len * k.width;
  const widths = pts.map((_, i) => {
    const t = i / (n - 1);
    const shape = t < 0.06 ? 0.55 + (t / 0.06) * 0.25 : t < 0.4 ? 0.8 + ((t - 0.06) / 0.34) * 0.2 : t < 0.68 ? 1 : 1 - ((t - 0.68) / 0.32) * 0.9;
    const bulge = bug.bulge === null ? 0 : 0.6 * Math.exp(-(((t - bug.bulge) / 0.06) ** 2));
    return W * shape * (1 + bulge);
  });
  const angle = (i) => {
    const a = pts[Math.max(0, i - 1)];
    const b = pts[Math.min(n - 1, i + 1)];
    return Math.atan2(a.y - b.y, a.x - b.x);
  };
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // The translucent shadow goes down in a few continuous runs, not per segment: overlapping caps would stack
  // the alpha into dark beads at every joint.
  ctx.strokeStyle = 'rgb(0 0 0 / 0.3)';
  for (let i = 0; i < n - 1; i += 8) {
    const end = Math.min(n - 1, i + 8);
    ctx.lineWidth = widths[Math.floor((i + end) / 2)] * 1.05;
    ctx.lineCap = i ? 'butt' : 'round';
    ctx.beginPath();
    ctx.moveTo(pts[i].x + 3, pts[i].y + 6);
    for (let j = i + 1; j <= end; j++) ctx.lineTo(pts[j].x + 3, pts[j].y + 6);
    ctx.stroke();
  }
  ctx.lineCap = 'round';

  // Variable-width body: each segment stroked at its own width, edge colour first, then the back on top.
  // `keep(i)` picks the segments from point i to i + 1 that get drawn.
  const body = (keep) => {
    for (const [color, scale] of [[c.edge, 1], [c.body, 0.8], [c.belly, 0.3]]) {
      ctx.strokeStyle = color;
      for (let i = 0; i < n - 1; i++) {
        if (!keep(i)) continue;
        ctx.lineWidth = ((widths[i] + widths[i + 1]) / 2) * scale;
        ctx.beginPath();
        ctx.moveTo(pts[i].x, pts[i].y);
        ctx.lineTo(pts[i + 1].x, pts[i + 1].y);
        ctx.stroke();
      }
    }

    // Ball python pattern: dark saddles stroked along the spine so they follow the body, rimmed in tan, with
    // small dark spots on the flanks between them
    for (let i = 3; i < n - 4; i += 4) {
      if (!keep(i) || !keep(i + 1)) continue;
      for (const [color, scale] of [[c.rim, 0.8], [c.blotch, 0.72]]) {
        ctx.strokeStyle = color;
        ctx.lineWidth = widths[i] * scale;
        ctx.beginPath();
        ctx.moveTo(pts[i].x, pts[i].y);
        ctx.lineTo(pts[i + 1].x, pts[i + 1].y);
        ctx.lineTo(pts[i + 2].x, pts[i + 2].y);
        ctx.stroke();
      }
      const j = i + 2;
      const a = angle(j);
      ctx.fillStyle = c.blotch;
      for (const side of [-1, 1]) {
        const off = side * widths[j] * 0.26;
        ctx.beginPath();
        ctx.ellipse(pts[j].x - Math.sin(a) * off, pts[j].y + Math.cos(a) * off, widths[j] * 0.16, widths[j] * 0.1, a, 0, TAU);
        ctx.fill();
      }
    }
  };
  body(() => true);
  if (held && bug.grip) {
    held.k.plan.draw(ctx, held, time);
    body((i) => pts[i].over && pts[i + 1].over);
  }

  // Head: wider than the neck, pale stripe through each eye, tongue flicking unless its mouth is full.
  // Swallowing, the jaws stretch wide over the rat.
  const a = angle(0);
  const gape = bug.state === 'swallow' && !bug.grip ? 1.3 : 1;
  ctx.save();
  ctx.translate(pts[0].x, pts[0].y);
  ctx.rotate(a);
  ctx.scale(1.25, 1.25 * gape);
  const flick = (time * 0.9 + bug.phase) % 1;
  if (!held && flick < 0.28) {
    const out = Math.sin((flick / 0.28) * Math.PI) * W * 1.4;
    const wag = Math.sin(time * 45) * 0.25;
    const tip = { x: W * 1.05 + out, y: Math.sin(wag) * out * 0.2 };
    ctx.strokeStyle = c.tongue;
    ctx.lineWidth = Math.max(1, W * 0.07);
    ctx.beginPath();
    ctx.moveTo(W * 0.9, 0);
    ctx.lineTo(tip.x, tip.y);
    ctx.lineTo(tip.x + W * 0.28, tip.y - W * 0.16);
    ctx.moveTo(tip.x, tip.y);
    ctx.lineTo(tip.x + W * 0.28, tip.y + W * 0.16);
    ctx.stroke();
  }
  const head = (grow) => {
    ctx.beginPath();
    ctx.moveTo(-W * 0.3, -W * 0.34 * grow);
    ctx.bezierCurveTo(W * 0.05, -W * 0.62 * grow, W * 0.55, -W * 0.6 * grow, W * 0.95, -W * 0.24 * grow);
    ctx.quadraticCurveTo(W * 1.12, 0, W * 0.95, W * 0.24 * grow);
    ctx.bezierCurveTo(W * 0.55, W * 0.6 * grow, W * 0.05, W * 0.62 * grow, -W * 0.3, W * 0.34 * grow);
    ctx.closePath();
    ctx.fill();
  };
  ctx.fillStyle = c.edge;
  head(1);
  ctx.fillStyle = c.head;
  head(0.8);
  ctx.strokeStyle = c.stripe;
  ctx.lineWidth = Math.max(0.8, W * 0.05);
  ctx.beginPath();
  for (const side of [-1, 1]) {
    ctx.moveTo(W * 0.9, side * W * 0.12);
    ctx.quadraticCurveTo(W * 0.6, side * W * 0.3, W * 0.45, side * W * 0.36);
    ctx.lineTo(-W * 0.15, side * W * 0.3);
  }
  ctx.stroke();
  for (const side of [-1, 1]) {
    ctx.fillStyle = '#0d0905';
    ctx.beginPath();
    ctx.arc(W * 0.58, side * W * 0.4, W * 0.12, 0, TAU);
    ctx.fill();
    ctx.fillStyle = 'rgb(255 245 220 / 0.7)';
    ctx.beginPath();
    ctx.arc(W * 0.61, side * W * 0.4 - W * 0.04, W * 0.04, 0, TAU);
    ctx.fill();
  }
  ctx.restore();
}

class BcCrawlers extends HTMLElement {
  #bugs = [];
  #covers = [];
  #threats = [];
  #size = { w: 0, h: 0 };
  #canvas = document.createElement('canvas');
  #ctx = this.#canvas.getContext('2d');
  #dpr = 1;
  #frame = 0;
  #last = 0;
  #time = 0;
  #visible = false;
  #pointer = null;
  #still = window.matchMedia('(prefers-reduced-motion: reduce)');
  #section = null;

  connectedCallback() {
    this.#section = this.closest('section, .shopify-section') || this.parentElement;
    const kinds = (attr) => (this.getAttribute(attr) || '').split(/\s+/).filter((name) => KINDS[name]);
    const cast = kinds('kinds');
    const lineup = cast.length ? cast : LINEUP;
    const count = Number(this.getAttribute('count') || cast.length || 3);
    const listed = [...Array.from({ length: count }, (_, i) => lineup[i % lineup.length]), ...kinds('also')];
    const brought = listed.flatMap((name) =>
      Object.entries(KINDS[name].brings || {}).flatMap(([kind, [lo, hi]]) => Array(Math.round(rand(lo, hi))).fill(kind))
    );
    const names = [...listed, ...brought];
    if (!names.length || !this.#section || !this.#ctx) return;
    this.append(this.#canvas);

    this.#measure();
    for (const [i, name] of names.entries()) this.#bugs.push(this.#spawn(name, i));
    this.#bugs.sort((a, b) => a.k.plan.layer - b.k.plan.layer);

    new ResizeObserver(() => this.#measure()).observe(this.#section);
    new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      if (this.#visible) this.#start();
    }).observe(this.#section);

    const aim = (e) => {
      const r = this.getBoundingClientRect();
      this.#pointer = { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    this.#section.addEventListener('pointermove', (e) => e.pointerType === 'mouse' && aim(e));
    this.#section.addEventListener('pointerdown', (e) => {
      aim(e);
      if (e.pointerType !== 'mouse') setTimeout(() => (this.#pointer = null), 400);
    });
    this.#section.addEventListener('pointerleave', () => (this.#pointer = null));
    this.#section.addEventListener('bc:startle', (e) => this.#startle(e.detail));
    document.addEventListener('visibilitychange', () => document.hidden || this.#start());
    this.#still.addEventListener('change', () => (this.#still.matches ? this.#drawStill() : this.#start()));
    if (this.#still.matches) this.#drawStill();
  }

  /** Everything that can flee scatters from a point given in client coordinates. */
  #startle(detail) {
    if (!Number.isFinite(detail?.x) || !Number.isFinite(detail?.y)) return;
    this.#measure();
    const r = this.getBoundingClientRect();
    const at = { x: detail.x - r.left, y: detail.y - r.top };
    for (const bug of this.#bugs) {
      if (!bug.k.plan.prey || !loose(bug)) continue;
      bug.threat = at;
      this.#go(bug, 'alarm');
    }
  }

  #measure() {
    const host = this.getBoundingClientRect();
    if (!host.width) return;
    this.#size = { w: host.width, h: host.height };
    this.#dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.#canvas.width = Math.round(host.width * this.#dpr);
    this.#canvas.height = Math.round(host.height * this.#dpr);
    // Only cover that overlaps the canvas: a bug hiding behind anything else would just vanish.
    this.#covers = [...this.#section.querySelectorAll('.bc-tub__box, img, [data-bc-cover]')]
      .map((el) => el.getBoundingClientRect())
      .filter((r) => r.width >= 60 && r.height >= 60 && r.right > host.left && r.left < host.right && r.bottom > host.top && r.top < host.bottom)
      .map((r) => ({ x: r.left - host.left, y: r.top - host.top, w: r.width, h: r.height }));
    if (this.#still.matches) this.#drawStill();
  }

  #spawn(kind, i) {
    const k = KINDS[kind];
    // Long bodies are capped to half the canvas so a snake still fits on a phone.
    const len = Math.min(rand(...k.len), this.#size.w * 0.5 || Infinity);
    const bug = { k, len, x: 0, y: 0, heading: 0, speed: 0, timer: 0, wriggle: 0, phase: Math.random(), air: 0, odo: 0, bulge: null, threat: null, grip: null };
    bug.hop = { t: 1, wait: rand(0.4, 2), dist: 0 };
    bug.winged = Math.random() < (k.winged || 0);
    if (bug.winged) bug.len *= 1.12;
    bug.feeler = rand(0, TAU);
    bug.wander = [rand(0.3, 0.7), rand(1.1, 1.9), rand(0, TAU)];

    // Larvae are too slow to arrive from an edge, so they start somewhere already. Half the legged bugs start
    // hidden in cover with their heads out, the rest wander in from the edges. Snakes always slither in.
    const start = k.plan.start;
    if (start === 'placed' || (start === 'mixed' && this.#covers.length && i % 2 === 0)) {
      this.#place(bug);
      this.#go(bug, 'rest');
      bug.timer = rand(1, 6);
    } else {
      this.#go(bug, 'away');
      bug.timer = rand(0.3, 3) + i * 0.6;
    }
    return bug;
  }

  #go(bug, state) {
    const from = bug.state;
    bug.state = state;
    STATES[state].enter?.(bug, from);
  }

  /** Puts a bug down at rest somewhere on the wall: peeking out of cover if it can, else anywhere. */
  #place(bug) {
    bug.target = this.#covers.length && bug.k.plan.peek ? this.#coverSpot(bug) : { ...this.pickTarget(bug, true), face: rand(0, TAU) };
    Object.assign(bug, { x: bug.target.x, y: bug.target.y, heading: bug.target.face });
    bug.k.plan.plant(bug);
  }

  /** An away bug comes back: placed bodies reappear at rest, the rest enter from a side edge. */
  comeBack(bug) {
    if (bug.k.plan.start === 'placed') {
      this.#place(bug);
      return 'rest';
    }
    const { w, h } = this.#size;
    const left = Math.random() < 0.5;
    const margin = Math.min(bug.len, 40);
    bug.x = left ? -margin : w + margin;
    bug.y = rand(h * 0.12, h * 0.88);
    bug.heading = left ? rand(-0.5, 0.5) : Math.PI + rand(-0.5, 0.5);
    bug.k.plan.plant(bug);
    bug.target = this.pickTarget(bug, true);
    return bug.k.plan.wake;
  }

  pickTarget(bug, stay = false) {
    const roll = Math.random();
    if (this.#covers.length && roll < 0.55) return this.#coverSpot(bug);
    if (!stay && roll > 0.82) return this.exit(bug, Math.random() < 0.5 ? -1 : 1);
    const { w, h } = this.#size;
    return { x: rand(w * 0.06, w * 0.94), y: rand(h * 0.08, h * 0.92) };
  }

  /** A spot just inside a cover's edge, facing out, so a legged bug's head shows. Others tuck in fully. */
  #coverSpot(bug, from = this.#covers) {
    const c = pick(from);
    const inset = bug.k.plan.peek ? bug.len * 0.5 : Math.min(bug.len * 1.1, c.w / 2);
    const side = pick(['left', 'right', 'right', 'left', 'top']);
    const along = (a, size) => a + size * rand(0.2, 0.8);
    if (side === 'left') return { x: c.x + inset, y: along(c.y, c.h), face: Math.PI + rand(-0.3, 0.3) };
    if (side === 'right') return { x: c.x + c.w - inset, y: along(c.y, c.h), face: rand(-0.3, 0.3) };
    return { x: along(c.x, c.w), y: c.y + inset, face: -Math.PI / 2 + rand(-0.3, 0.3) };
  }

  /** Off a side edge, the nearer one unless told. */
  exit(bug, dir = bug.x < this.#size.w / 2 ? -1 : 1) {
    const { w } = this.#size;
    return { x: dir < 0 ? -bug.len * 2 : w + bug.len * 2, y: clamp(bug.y + rand(-120, 120), 0, this.#size.h), exit: true };
  }

  /** Nearest cover that lies away from the bug's threat, or off the nearer side edge. */
  escape(bug) {
    const p = bug.threat || { x: bug.x - Math.cos(bug.heading), y: bug.y - Math.sin(bug.heading) };
    const away = { x: bug.x - p.x, y: bug.y - p.y };
    const ahead = this.#covers.filter((c) => (c.x + c.w / 2 - bug.x) * away.x + (c.y + c.h / 2 - bug.y) * away.y > 0);
    if (ahead.length) {
      ahead.sort((a, b) => dist(bug, { x: a.x + a.w / 2, y: a.y + a.h / 2 }) - dist(bug, { x: b.x + b.w / 2, y: b.y + b.h / 2 }));
      return this.#coverSpot(bug, ahead.slice(0, 2));
    }
    return this.exit(bug, away.x < 0 ? -1 : 1);
  }

  /**
   * The nearest spot, searched in rings out to 320px, where everything within `r` is on the canvas and clear of
   * cover, or null where there is none.
   */
  clearing(p, r) {
    const { w, h } = this.#size;
    const open = (x, y) => x > r && x < w - r && y > r && y < h - r &&
      this.#covers.every((c) => x < c.x - r || x > c.x + c.w + r || y < c.y - r || y > c.y + c.h + r);
    for (let d = 0; d <= 320; d += 16) {
      for (let k = 0; k < 16; k++) {
        const x = p.x + Math.cos((k / 16) * TAU) * d;
        const y = p.y + Math.sin((k / 16) * TAU) * d;
        if (open(x, y)) return { x, y };
      }
    }
    return null;
  }

  nearestPrey(snake) {
    let best = null;
    let bestD = Infinity;
    for (const bug of this.#bugs) {
      if (!bug.k.plan.quarry || !loose(bug)) continue;
      const d = dist(snake, bug);
      if (d < bestD) [best, bestD] = [bug, d];
    }
    return best;
  }

  /** The last of the rat goes down: it comes back later from an edge, and the fed snake leaves. */
  eat(snake, prey) {
    this.#go(prey, 'away');
    prey.timer = rand(6, 11);
    snake.bulge = 0.03;
    snake.timer = 0;
  }

  /** Turns toward the target and moves; returns true on arrival. */
  steer(bug, dt, speed, turnBoost = 1) {
    const { k } = bug;
    const want = Math.atan2(bug.target.y - bug.y, bug.target.x - bug.x);
    const [f1, f2, f0] = bug.wander;
    let wobble = 0;
    if (speed && k.slither) wobble = k.slither[0] * Math.min(1, 160 / speed) * Math.sin((bug.odo / (k.slither[1] * bug.len)) * TAU);
    else if (speed) wobble = 0.45 * Math.sin(this.#time * f1 * TAU + f0) + 0.2 * Math.sin(this.#time * f2 * TAU);
    const diff = wrap(want + wobble - bug.heading);
    const turn = k.turn * turnBoost * dt;
    if (speed && !bug.air) bug.heading += clamp(diff, -turn, turn);

    const v = k.plan.pace(bug, diff, speed, dt);
    bug.x += Math.cos(bug.heading) * v * dt;
    bug.y += Math.sin(bug.heading) * v * dt;
    bug.v = v;
    bug.odo += v * dt;
    return dist(bug, bug.target) < Math.max(4, bug.len * k.plan.arrive);
  }

  #start() {
    if (this.#frame || this.#still.matches || !this.#size.w) return;
    this.#last = performance.now();
    this.#frame = requestAnimationFrame(this.#tick);
  }

  #tick = (now) => {
    const dt = Math.min((now - this.#last) / 1000, 0.05);
    this.#last = now;
    this.#time += dt;
    this.#threats = this.#bugs.filter((bug) => !bug.k.plan.prey && bug.state !== 'away');
    if (this.#pointer) this.#threats.push(this.#pointer);
    for (const bug of this.#bugs) this.#step(bug, dt);
    this.#draw();
    this.#frame = this.#visible && !document.hidden && !this.#still.matches ? requestAnimationFrame(this.#tick) : 0;
  };

  #step(bug, dt) {
    const { plan } = bug.k;
    bug.v = 0;
    bug.phase = (bug.phase + dt * (bug.k.wave || 0)) % 1;
    bug.wriggle = Math.max(0, bug.wriggle - dt * 0.9);
    bug.feeler += dt * (bug.state === 'pause' || bug.state === 'rest' ? 3.4 : 1.6);

    if (plan.prey && CALM.has(bug.state)) {
      const head = toWorld(bug, bug.len * 0.5, 0);
      const range = bug.state === 'rest' ? 55 : 95;
      const threat = this.#threats.find((t) => dist(head, t) < range);
      if (threat) {
        bug.threat = threat;
        this.#go(bug, 'alarm');
      }
    }

    const next = STATES[bug.state].step(bug, dt, this);
    if (next) this.#go(bug, next);
    if (bug.state !== 'away') plan.move(bug, dt);
  }

  #drawStill() {
    if (!this.#size.w) return;
    for (const bug of this.#bugs) {
      if (bug.state !== 'away') continue;
      this.#place(bug);
      bug.state = 'rest';
    }
    this.#draw();
  }

  #draw() {
    const ctx = this.#ctx;
    ctx.setTransform(this.#dpr, 0, 0, this.#dpr, 0, 0);
    ctx.clearRect(0, 0, this.#size.w, this.#size.h);
    for (const bug of this.#bugs) if (loose(bug)) bug.k.plan.draw(ctx, bug, this.#time);
  }
}

if (!customElements.get('bc-crawlers')) customElements.define('bc-crawlers', BcCrawlers);
